"use client";

import { useState } from "react";
import emailjs from "@emailjs/browser";

const SERVICE_ID = "service_h6xav4e";
const TEMPLATE_ID = "template_s3slzs5";
const PUBLIC_KEY = "XB3SWie53KWlVskM5";

const COUNTRY_CODES = [
  { code: "+1", label: "US +1" },
  { code: "+91", label: "IN +91" },
  { code: "+44", label: "UK +44" },
  { code: "+61", label: "AU +61" },
  { code: "+971", label: "AE +971" },
  { code: "+65", label: "SG +65" },
];

const INITIAL = {
  name: "",
  email: "",
  countryCode: "+1",
  mobile: "",
  comments: "",
};

// Custom chevron so there is breathing room between the arrow and the right border.
const SELECT_ARROW_STYLE = {
  backgroundImage:
    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23374151' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\")",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 0.9rem center",
  backgroundSize: "12px 12px",
};

const inputClass = (r) =>
  `w-full ${r} border border-gray-300 px-4 py-3 text-base placeholder:text-gray-500 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200`;

/**
 * Shared lead form (fields, validation, EmailJS submission, success screen).
 * Used by the popup (StaticFormWidget) and inline on the contact page.
 *
 * - source:   context string sent along in the email
 * - heading:  title shown above the form
 * - radius:   Tailwind rounded-* class applied to the input fields
 * - onClose:  optional; when given the success screen shows a "Close" button
 *             that calls it, otherwise it shows "Submit another response".
 */
export default function LeadFormFields({
  source = "default",
  heading = "Start Your Journey",
  onClose = undefined,
  radius = "rounded-md",
  className = "",
}) {
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | success | error

  const reset = () => {
    setForm(INITIAL);
    setErrors({});
    setStatus("idle");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Name is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      next.email = "Enter a valid email";
    if (!/^\d{7,15}$/.test(form.mobile.replace(/[\s-]/g, "")))
      next.mobile = "Enter a valid mobile number";
    return next;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length) return;

    setStatus("sending");
    try {
      await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        {
          name: form.name.trim(),
          email: form.email.trim(),
          mobile: `${form.countryCode} ${form.mobile.trim()}`,
          comments: form.comments.trim() || "-",
          source,
          page: window.location.href,

          //   to_email: "support@innovoot.com"

          to_email: "training@seasoftclasses.com",
        },
        PUBLIC_KEY
      );
      setStatus("success");
    } catch (err) {
      console.error("Form submission failed:", err);
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div className={`p-8 text-center ${className}`}>
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-3xl text-green-600">
          ✓
        </div>
        <h3 className="mb-2 text-xl font-semibold text-slate-900">
          Form submitted successfully!
        </h3>
        <p className="mb-6 text-sm text-slate-600">
          Thank you, {form.name}. We have received your details and will get
          back to you shortly.
        </p>
        <button
          type="button"
          onClick={onClose || reset}
          className={`${radius} bg-blue-600 px-5 py-2 text-sm font-medium text-white hover:bg-blue-700`}
        >
          {onClose ? "Close" : "Submit another response"}
        </button>
      </div>
    );
  }

  return (
    <div className={`p-6 ${className}`}>
      <h3 className="mb-10 text-lg font-semibold text-slate-900">{heading}</h3>
      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        <div>
          <label htmlFor="sf-name" className="mb-1 block text-sm font-medium">
            Name <span className="text-red-600">*</span>
          </label>
          <input
            id="sf-name"
            name="name"
            type="text"
            value={form.name}
            onChange={handleChange}
            className={inputClass(radius)}
            placeholder="Your name"
          />
          {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
        </div>

        <div>
          <label htmlFor="sf-email" className="mb-1 block text-sm font-medium">
            Email <span className="text-red-600">*</span>
          </label>
          <input
            id="sf-email"
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            className={inputClass(radius)}
            placeholder="you@example.com"
          />
          {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
        </div>

        <div>
          <label htmlFor="sf-mobile" className="mb-1 block text-sm font-medium">
            Mobile Number <span className="text-red-600">*</span>
          </label>
          <div className="flex gap-2">
            <select
              name="countryCode"
              value={form.countryCode}
              onChange={handleChange}
              aria-label="Country code"
              className={`appearance-none ${radius} border border-gray-300 bg-white pl-3 pr-10 py-3 text-base focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200`}
              style={SELECT_ARROW_STYLE}
            >
              {COUNTRY_CODES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </select>
            <input
              id="sf-mobile"
              name="mobile"
              type="tel"
              inputMode="tel"
              value={form.mobile}
              onChange={handleChange}
              className={inputClass(radius)}
              placeholder="Mobile number"
            />
          </div>
          {errors.mobile && <p className="mt-1 text-xs text-red-600">{errors.mobile}</p>}
        </div>

        <div>
          <label htmlFor="sf-comments" className="mb-1 block text-sm font-medium">
            Comments
          </label>
          <textarea
            id="sf-comments"
            name="comments"
            rows={4}
            value={form.comments}
            onChange={handleChange}
            className={inputClass(radius)}
            placeholder="Your comments"
          />
        </div>

        {status === "error" && (
          <p className="text-sm text-red-600">
            Something went wrong while sending. Please try again.
          </p>
        )}

        <button
          type="submit"
          disabled={status === "sending"}
          className={`w-full ${radius} bg-blue-600 px-4 py-3 text-base font-semibold text-white hover:bg-blue-700 disabled:opacity-60`}
        >
          {status === "sending" ? "Submitting..." : "Submit"}
        </button>
      </form>
    </div>
  );
}
